import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { PkField } from './pk-field';
import { PkFieldBase } from './field-base';
import { applyFormat, type FormatKind } from '../../util/biz-format';

/** 텍스트 입력 — 라벨·필수 표시·에러 메시지 래퍼 포함 (CVA) */
@Component({
  selector: 'pk-input',
  standalone: true,
  imports: [PkField],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <pk-field
      [label]="label()"
      [required]="isRequired()"
      [error]="errorText()"
      [hint]="hint()"
      [forId]="fieldId"
    >
      <input
        class="pk-control"
        [id]="fieldId"
        [type]="type()"
        [value]="displayValue()"
        [disabled]="disabled()"
        [class.pk-control--error]="errorText() !== null"
        [attr.placeholder]="placeholder() ?? null"
        [attr.maxlength]="maxLength() ?? null"
        [attr.autocomplete]="autocomplete() ?? null"
        (input)="onInput($event)"
        (blur)="onBlur()"
      />
    </pk-field>
  `,
})
export class PkInput extends PkFieldBase<string> {
  readonly type = input<'text' | 'password' | 'email' | 'tel' | 'number'>('text');
  readonly placeholder = input<string | null>(null);
  readonly maxLength = input<number | null>(null);
  readonly autocomplete = input<string | null>(null);
  /**
   * as-you-type 하이픈 포맷 — 지정하면 **폼 값은 숫자만**, 입력창에는 하이픈이 들어간 표시값이
   * 보인다(전화 `02-1234-5678`, 사업자 `000-00-00000`, 법인 `000000-0000000`). null이면 예전
   * 그대로 입력값을 손대지 않는다(다른 화면의 DOM·값 불변).
   */
  readonly format = input<FormatKind | null>(null);
  /** blur 시 후처리용 (인덱스 0 패딩 등) */
  readonly blurred = output<void>();

  /** 입력창에 실제로 써 넣는 값 — format이 있으면 숫자 값에서 매번 다시 만든다 */
  protected readonly displayValue = computed(() => {
    const kind = this.format();
    const value = this.value();
    return kind === null ? value : applyFormat(kind, value).display;
  });

  protected defaultValue(): string {
    return '';
  }

  /** writeValue — format 모드에선 하이픈 섞인 옛 저장값(`02-000-0000`)도 숫자로 정규화해 든다 */
  protected coerceValue(raw: unknown): string {
    const text = raw == null ? '' : String(raw);
    const kind = this.format();
    return kind === null ? text : applyFormat(kind, text).digits;
  }

  protected onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const kind = this.format();
    if (kind === null) {
      this.commit(el.value);
      return;
    }
    const { digits, display } = applyFormat(kind, el.value);
    // 표시값은 여기서 바로 써 넣는다(커서는 끝으로). 이후 `[value]` 바인딩이 같은 문자열을
    // 다시 쓰지만 같은 값이라 커서가 움직이지 않는다.
    if (el.value !== display) {
      el.value = display;
    }
    this.commit(digits);
  }

  protected onBlur(): void {
    this.markTouched();
    this.blurred.emit();
  }
}
